import type { DateValidationError } from '@mui/x-date-pickers';
import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import { LeaseDatePicker } from './lease.date-picker';

type LeaseEndDatePickerProps = {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  watch: UseFormWatch<Renter>;
  endDateManuallyEdited: React.MutableRefObject<boolean>;
};

export const LeaseEndDatePicker = ({
  control,
  errors,
  setValue,
  trigger,
  watch,
  endDateManuallyEdited,
}: LeaseEndDatePickerProps) => {
  const error = errors.leaseEnd;
  const leaseStart = watch('leaseStart');

  const onChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue !== null && !newValue.isValid()) {
      return;
    }

    const formattedValue = newValue ? newValue.toDate() : null;

    if (formattedValue) {
      endDateManuallyEdited.current = true;
    } else {
      endDateManuallyEdited.current = false;
    }

    setValue('leaseEnd', formattedValue as Date, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    trigger('leaseEnd');
    trigger('leaseStart');
  };

  const onError = (_validationError: DateValidationError, value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setValue('leaseEnd', value.toDate());
    }
  };

  return (
    <Controller
      name="leaseEnd"
      control={control}
      render={({ field }) => {
        const pickerValue = field.value ? dayjs(field.value) : null;

        return (
          <LeaseDatePicker
            title="Lease end date"
            value={pickerValue}
            onChange={onChange}
            onError={onError}
            error={error}
            required
            dataTestId="lease_end"
            minDate={leaseStart ? dayjs(leaseStart).add(1, 'day') : undefined}
            maxDate={leaseStart ? dayjs(leaseStart).add(10, 'year').add(1, 'month') : undefined}
          />
        );
      }}
    />
  );
};
